import {
  SEED_MARKETS,
  marketsForScope,
  type Click,
  type Market,
  type Merchant,
  type MerchantRegion,
} from '../dataProvider/types';
import { MATURATION_DAYS, CLICK_FLOOR } from '../reports/maturation';

/**
 * The Store performance query/aggregation module - the ONE place the funnel is
 * computed. Same seam idiom as earnings/earningsModel.ts: v1 aggregates
 * client-side from the mock provider, every selector lives here, and the late
 * real-data migration swaps this module for a server-side endpoint without the
 * surface changing. The surface never sees a raw click.
 *
 * It ranks STORES (a store = a merchantRegion, the per-market store) by click and
 * conversion volume, and surfaces the stores that pull real traffic but convert
 * below par. A brand that operates in two markets is two stores, one row per
 * band, never merged.
 *
 * THE MATURATION CUTOFF (the core mechanic). A click converts days or weeks after
 * it happens, so recent clicks have not had time to convert and must not drag the
 * conversion rate down. The rate is therefore computed only over MATURED clicks:
 * clicks whose occurredAt is at least MATURATION_DAYS old. Younger clicks are
 * excluded from BOTH the numerator and the denominator of the rate, so a fresh
 * store never reads as a leak. The volume columns (clicks, conversions) still show
 * the full selected period. Click age is measured from occurredAt, NOT createdAt.
 *
 * CONVERSION SOURCE OF TRUTH is the click's `converted` boolean, counted once per
 * click. The surface deliberately does NOT rank off the transactions.clickId join
 * (D-store-performance): the boolean is the mock path; the transaction link is the
 * real-data path, where conversion time and value also become available.
 */

// --- tunables (flagged for review) -----------------------------------------

// The maturation cutoff and the click floor are SHARED with Exit clicks (the
// other funnel surface), so they live in one place and are re-exported here for
// the callers that read them from this model. See src/reports/maturation.ts.
export { MATURATION_DAYS, CLICK_FLOOR } from '../reports/maturation';

// --- types ------------------------------------------------------------------

/** The period control. Opens on last 30 days rolling (D-store-performance). */
export type StorePerfPeriod = 'last30' | 'mtd' | 'lastMonth';

export const PERIOD_OPTIONS: ReadonlyArray<{
  id: StorePerfPeriod;
  label: string;
}> = [
  { id: 'last30', label: 'Last 30 days' },
  { id: 'mtd', label: 'Month to date' },
  { id: 'lastMonth', label: 'Last full calendar month' },
];

export interface StorePerfRow {
  /** Stable key, e.g. `region:10`. */
  key: string;
  /** The parent merchant's display name. */
  name: string;
  /** Clicks in the selected period (full period, matured or not). */
  clicks: number;
  /** Converted clicks in the selected period (the `converted` boolean). */
  conversions: number;
  /** Matured clicks in the period (the rate denominator). */
  maturedClicks: number;
  /** Matured converted clicks (the rate numerator). */
  maturedConversions: number;
  /** maturedConversions / maturedClicks, or null when no matured clicks. */
  conversionRate: number | null;
  /** True when maturedClicks is below the floor (kept out of the ranked set). */
  belowFloor: boolean;
}

export interface StorePerfBand {
  market: Market;
  /** Stores at or above the click floor, sorted by conversion rate ascending. */
  ranked: StorePerfRow[];
  /** Stores below the floor (includes fresh, no-rate stores), unranked. */
  subFloor: StorePerfRow[];
  /** Band norm: the median conversion rate over ranked stores, or null. */
  medianRate: number | null;
}

export interface StorePerfView {
  bands: StorePerfBand[];
}

export interface StorePerfInput {
  clicks: Click[];
  merchants: Merchant[];
  merchantRegions: MerchantRegion[];
}

export interface StorePerfOptions {
  /** Access scope (UK | US | GLOBAL); decides which market bands render. */
  scope: string;
  period: StorePerfPeriod;
  /** Reference clock (epoch ms) for the period window and the maturation cutoff. */
  now: number;
}

// --- helpers ----------------------------------------------------------------

const DAY = 86_400_000;

/** The market bands a scope renders: scope -> markets, kept to operating markets. */
export const bandsForScope = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

/** Inclusive [start, end] ms window for the selected period (same as Earnings). */
function periodWindow(
  period: StorePerfPeriod,
  now: number,
): { start: number; end: number } {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  switch (period) {
    case 'last30':
      return { start: now - 30 * DAY, end: now };
    case 'mtd':
      return { start: Date.UTC(y, m, 1), end: now };
    case 'lastMonth':
      return { start: Date.UTC(y, m - 1, 1), end: Date.UTC(y, m, 1) - 1 };
  }
}

const inWindow = (ts: number, w: { start: number; end: number }): boolean =>
  ts >= w.start && ts <= w.end;

const median = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

interface Accum {
  name: string;
  clicks: number;
  conversions: number;
  maturedClicks: number;
  maturedConversions: number;
}

// --- aggregation ------------------------------------------------------------

/** Build the full view for the active scope (one band per derived market). */
export function buildStorePerformanceView(
  input: StorePerfInput,
  opts: StorePerfOptions,
): StorePerfView {
  const bands = bandsForScope(opts.scope).map((market) =>
    buildBand(market, input, opts),
  );
  return { bands };
}

function buildBand(
  market: Market,
  input: StorePerfInput,
  opts: StorePerfOptions,
): StorePerfBand {
  const regionById = new Map(input.merchantRegions.map((r) => [r.id, r]));
  const merchantById = new Map(input.merchants.map((m) => [m.id, m]));

  const window = periodWindow(opts.period, opts.now);
  // A click is matured when its occurredAt is at least MATURATION_DAYS old,
  // measured from `now` (not from the window edge).
  const maturityCutoff = opts.now - MATURATION_DAYS * DAY;

  // Clicks for this market that fall in the selected period.
  const clicks = input.clicks.filter(
    (c) => c.market === market && inWindow(Date.parse(c.occurredAt), window),
  );

  // Group by merchantRegion (the per-market store).
  const accums = new Map<number, Accum>();
  for (const c of clicks) {
    const regionId = c.merchantRegionId;
    const region = regionById.get(regionId);
    const merchantId = region?.merchantId ?? c.merchantId;
    const name =
      merchantById.get(merchantId)?.name ?? `Store ${String(regionId)}`;
    const acc = accums.get(regionId) ?? {
      name,
      clicks: 0,
      conversions: 0,
      maturedClicks: 0,
      maturedConversions: 0,
    };
    acc.clicks += 1;
    // Conversion source of truth is the click's `converted` boolean, never the
    // transactions.clickId join (D-store-performance).
    if (c.converted) acc.conversions += 1;
    const matured = Date.parse(c.occurredAt) <= maturityCutoff;
    if (matured) {
      acc.maturedClicks += 1;
      if (c.converted) acc.maturedConversions += 1;
    }
    accums.set(regionId, acc);
  }

  const rows: StorePerfRow[] = [...accums.entries()].map(([regionId, a]) => ({
    key: `region:${String(regionId)}`,
    name: a.name,
    clicks: a.clicks,
    conversions: a.conversions,
    maturedClicks: a.maturedClicks,
    maturedConversions: a.maturedConversions,
    conversionRate:
      a.maturedClicks > 0 ? a.maturedConversions / a.maturedClicks : null,
    belowFloor: a.maturedClicks < CLICK_FLOOR,
  }));

  const ranked = rows
    .filter((r) => !r.belowFloor)
    // Default: conversion rate ascending, so the worst-converting stores with
    // real traffic (the leak-hunt) sit on top. Ranked rows always have a rate.
    .sort((a, b) => (a.conversionRate ?? 0) - (b.conversionRate ?? 0));
  const subFloor = rows
    .filter((r) => r.belowFloor)
    .sort((a, b) => a.name.localeCompare(b.name));

  const medianRate = median(
    ranked.map((r) => r.conversionRate).filter((v): v is number => v != null),
  );

  return { market, ranked, subFloor, medianRate };
}
